import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    ViewChild
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

export type ImageEditorTool = 'free' | 'line' | 'arrow' | 'rect' | 'circle';

export interface ImageEditorPoint {
    x: number;
    y: number;
}

export interface ImageEditorShape {
    type: ImageEditorTool;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    /** Only used by the free draw tool. */
    points?: ImageEditorPoint[];
    color: string;
    width: number;
}

export interface ImageEditorRect {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface ImageEditorResult {
    dataUrl: string;
    blob: Blob;
    file: File;
    rotation: number;      // total rotation applied inside the editor (already baked in)
    annotated: boolean;    // true when at least one shape was drawn
    cropped: boolean;      // true when the image was cropped
}

/**
 * Standalone, dependency free (plain canvas / vanilla JS) image editor modal.
 *
 * Open it with NgbModal and feed it an image, it hands back the edited image as
 * a dataUrl / Blob / File:
 *
 *   const ref = this.modalService.open(ImageEditorComponent, { size: 'lg', windowClass: 'image-editor-modal' });
 *   ref.componentInstance.imageSrc = someDataUrlOrUrl;
 *   ref.componentInstance.fileName = 'photo.jpg';
 *   ref.componentInstance.mimeType = 'image/jpeg';
 *   ref.result.then((res: ImageEditorResult) => { ... }).catch(() => {});
 */
@Component({
    selector: 'app-image-editor',
    templateUrl: './image-editor.component.html',
    styleUrls: ['./image-editor.component.scss']
})
export class ImageEditorComponent implements OnInit, AfterViewInit {

    /** Source image: data url, blob url or (same origin) http url. */
    @Input() imageSrc: string;
    /** Name given to the File handed back on apply. */
    @Input() fileName = 'image.png';
    /** Output mime type. Falls back to png when the browser cannot encode it. */
    @Input() mimeType = 'image/png';
    /** Rotation (deg) already staged outside the editor, baked in on load. */
    @Input() initialRotation = 0;
    /** Modal title. */
    @Input() title = 'Edit Image';

    @ViewChild('stage') stageRef: ElementRef;
    @ViewChild('baseCanvas') baseCanvasRef: ElementRef;
    @ViewChild('overlayCanvas') overlayCanvasRef: ElementRef;

    /** Largest side we keep while editing, keeps huge photos from choking canvas. */
    private static readonly MAX_SIDE = 4000;
    /** Smallest crop we allow, in image pixels. */
    private static readonly MIN_CROP = 24;
    /** localStorage key holding the last stroke width the inspector picked. */
    private static readonly WIDTH_KEY = 'imageEditor.strokeWidth';

    tool: ImageEditorTool = 'arrow';
    color = '#ff2d2d';
    strokeWidth = 16;

    /** Toolbar tools. `path` is the `d` of a 24x24 stroke-only svg icon. */
    readonly tools: Array<{ id: ImageEditorTool, label: string, path: string }> = [
        { id: 'free', label: 'Free Draw', path: 'M3 16c1.8 0 2.4-8 4.5-8S10 16 12 16s2.5-8 4.5-8S19 14 21 14' },
        { id: 'line', label: 'Line', path: 'M4 20L20 4' },
        { id: 'arrow', label: 'Arrow', path: 'M4 20L20 4M20 4l-7.4 1.1M20 4l-1.1 7.4' },
        { id: 'rect', label: 'Square', path: 'M4 5h16v14H4z' },
        { id: 'circle', label: 'Circle', path: 'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z' }
    ];

    readonly palette = [
        { value: '#ff2d2d', label: 'Red' },
        { value: '#ff9500', label: 'Orange' },
        { value: '#ffdd00', label: 'Yellow' },
        { value: '#2ecc40', label: 'Green' },
        { value: '#0a84ff', label: 'Blue' },
        { value: '#ffffff', label: 'White' },
        { value: '#111111', label: 'Black' }
    ];

    /** Stroke widths in image pixels. `dot` is the preview swatch size in css px. */
    readonly widths = [
        { value: 10, label: 'Small', dot: 6 },
        { value: 16, label: 'Medium', dot: 9 },
        { value: 24, label: 'Large', dot: 12 },
        { value: 34, label: 'Extra Large', dot: 15 },
        { value: 48, label: 'Huge', dot: 18 }
    ];

    loading = true;
    loadFailed = false;
    saving = false;

    cropMode = false;
    cropCursor = 'crosshair';

    private shapes: ImageEditorShape[] = [];
    /** Offscreen canvas holding the (rotated / cropped) source image without annotations. */
    private imageCanvas: HTMLCanvasElement;
    private rotation = 0;
    private cropped = false;
    private drawing = false;
    private start = { x: 0, y: 0 };
    private current = { x: 0, y: 0 };
    private freePoints: ImageEditorPoint[] = [];
    private shiftKey = false;

    private cropRect: ImageEditorRect = { x: 0, y: 0, w: 0, h: 0 };
    private cropDrag: { handle: string, from: ImageEditorPoint, orig: ImageEditorRect } = null;

    constructor(public activeModal: NgbActiveModal, private cdr: ChangeDetectorRef) { }

    ngOnInit() {
        if (!this.mimeType || this.mimeType === 'image/gif') {
            // canvas cannot encode gif, png is lossless and always supported
            this.mimeType = 'image/png';
        }
        this.restoreStrokeWidth();
    }

    ngAfterViewInit() {
        this.loadImage();
    }

    // ---------------------------------------------------------------- loading

    private loadImage() {
        if (!this.imageSrc) {
            this.loading = false;
            this.loadFailed = true;
            return;
        }

        const img = new Image();
        if (this.isCrossOrigin(this.imageSrc)) {
            img.crossOrigin = 'anonymous';
        }

        img.onload = () => {
            const scale = Math.min(
                1,
                ImageEditorComponent.MAX_SIDE / Math.max(img.width || 1, img.height || 1)
            );
            const w = Math.max(1, Math.round((img.width || 1) * scale));
            const h = Math.max(1, Math.round((img.height || 1) * scale));

            this.imageCanvas = document.createElement('canvas');
            this.imageCanvas.width = w;
            this.imageCanvas.height = h;
            this.imageCanvas.getContext('2d').drawImage(img, 0, 0, w, h);

            const steps = ((Math.round(this.initialRotation / 90) % 4) + 4) % 4;
            for (let i = 0; i < steps; i++) {
                this.rotateImageCanvas(1);
            }
            this.rotation = steps * 90;

            this.loading = false;
            this.loadFailed = false;
            // the canvases stay [hidden] while loading, flush that out before
            // measuring the stage so fitToStage sees real dimensions
            this.cdr.detectChanges();
            this.syncCanvasSize();
            this.redraw();
        };

        img.onerror = () => {
            this.loading = false;
            this.loadFailed = true;
            this.cdr.detectChanges();
        };

        img.src = this.imageSrc;
    }

    private isCrossOrigin(src: string): boolean {
        if (!src || src.indexOf('http') !== 0) {
            return false;
        }
        return src.indexOf(window.location.origin) !== 0;
    }

    // ----------------------------------------------------------------- canvas

    private get baseCanvas(): HTMLCanvasElement {
        return this.baseCanvasRef ? this.baseCanvasRef.nativeElement : null;
    }

    private get overlayCanvas(): HTMLCanvasElement {
        return this.overlayCanvasRef ? this.overlayCanvasRef.nativeElement : null;
    }

    private syncCanvasSize() {
        if (!this.imageCanvas || !this.baseCanvas || !this.overlayCanvas) {
            return;
        }
        const w = this.imageCanvas.width;
        const h = this.imageCanvas.height;

        [this.baseCanvas, this.overlayCanvas].forEach((c) => {
            c.width = w;
            c.height = h;
        });

        this.fitToStage();
    }

    /** CSS-scales the canvases so the whole image stays visible inside the stage. */
    private fitToStage() {
        if (!this.imageCanvas || !this.stageRef || !this.baseCanvas) {
            return;
        }
        const stage: HTMLElement = this.stageRef.nativeElement;
        const availW = Math.max(80, stage.clientWidth - 8);
        const availH = Math.max(80, stage.clientHeight - 8);
        const scale = Math.min(availW / this.imageCanvas.width, availH / this.imageCanvas.height, 1);

        const cssW = Math.round(this.imageCanvas.width * scale) + 'px';
        const cssH = Math.round(this.imageCanvas.height * scale) + 'px';

        [this.baseCanvas, this.overlayCanvas].forEach((c) => {
            c.style.width = cssW;
            c.style.height = cssH;
        });
    }

    /** How many image pixels one on-screen pixel covers. */
    private get displayRatio(): number {
        const canvas = this.overlayCanvas;
        if (!canvas) {
            return 1;
        }
        const rect = canvas.getBoundingClientRect();
        return rect.width ? canvas.width / rect.width : 1;
    }

    @HostListener('window:resize')
    onWindowResize() {
        this.fitToStage();
        this.redraw();
    }

    private redraw() {
        const canvas = this.baseCanvas;
        if (!canvas || !this.imageCanvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(this.imageCanvas, 0, 0);
        this.shapes.forEach((shape) => this.drawShape(ctx, shape));

        this.clearOverlay();
        if (this.cropMode) {
            this.drawCropOverlay();
        }
    }

    private clearOverlay() {
        const canvas = this.overlayCanvas;
        if (!canvas) {
            return;
        }
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    }

    // ---------------------------------------------------------------- drawing

    private drawShape(ctx: CanvasRenderingContext2D, shape: ImageEditorShape) {
        ctx.save();
        ctx.strokeStyle = shape.color;
        ctx.fillStyle = shape.color;
        ctx.lineWidth = shape.width;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        switch (shape.type) {
            case 'free':
                this.drawFreehand(ctx, shape);
                break;

            case 'line':
                ctx.beginPath();
                ctx.moveTo(shape.x1, shape.y1);
                ctx.lineTo(shape.x2, shape.y2);
                ctx.stroke();
                break;

            case 'arrow':
                this.drawArrow(ctx, shape);
                break;

            case 'rect':
                ctx.beginPath();
                ctx.rect(
                    Math.min(shape.x1, shape.x2),
                    Math.min(shape.y1, shape.y2),
                    Math.abs(shape.x2 - shape.x1),
                    Math.abs(shape.y2 - shape.y1)
                );
                ctx.stroke();
                break;

            case 'circle':
                this.drawEllipse(ctx, shape);
                break;
        }

        ctx.restore();
    }

    private drawFreehand(ctx: CanvasRenderingContext2D, shape: ImageEditorShape) {
        const pts = shape.points || [];
        if (!pts.length) {
            return;
        }
        if (pts.length === 1) {
            // a single tap still deserves a dot
            ctx.beginPath();
            ctx.arc(pts[0].x, pts[0].y, shape.width / 2, 0, Math.PI * 2);
            ctx.fill();
            return;
        }

        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        // midpoint quadratics smooth out the raw pointer samples
        for (let i = 1; i < pts.length - 1; i++) {
            const mx = (pts[i].x + pts[i + 1].x) / 2;
            const my = (pts[i].y + pts[i + 1].y) / 2;
            ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
        }
        ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
        ctx.stroke();
    }

    private drawArrow(ctx: CanvasRenderingContext2D, shape: ImageEditorShape) {
        const dx = shape.x2 - shape.x1;
        const dy = shape.y2 - shape.y1;
        const angle = Math.atan2(dy, dx);
        const len = Math.sqrt(dx * dx + dy * dy);
        const head = Math.min(Math.max(shape.width * 3.5, 12), Math.max(len, 1));
        // stop the shaft short of the tip so the solid head does not look blunt
        const shaftX = shape.x2 - Math.cos(angle) * head * 0.6;
        const shaftY = shape.y2 - Math.sin(angle) * head * 0.6;
        const longEnough = len > head * 0.6;

        ctx.beginPath();
        ctx.moveTo(shape.x1, shape.y1);
        ctx.lineTo(longEnough ? shaftX : shape.x1, longEnough ? shaftY : shape.y1);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(shape.x2, shape.y2);
        ctx.lineTo(
            shape.x2 - head * Math.cos(angle - Math.PI / 7),
            shape.y2 - head * Math.sin(angle - Math.PI / 7)
        );
        ctx.lineTo(
            shape.x2 - head * Math.cos(angle + Math.PI / 7),
            shape.y2 - head * Math.sin(angle + Math.PI / 7)
        );
        ctx.closePath();
        ctx.fill();
    }

    /** ctx.ellipse is not available everywhere, arc on a scaled context is. */
    private drawEllipse(ctx: CanvasRenderingContext2D, shape: ImageEditorShape) {
        const cx = (shape.x1 + shape.x2) / 2;
        const cy = (shape.y1 + shape.y2) / 2;
        const rx = Math.max(Math.abs(shape.x2 - shape.x1) / 2, 0.1);
        const ry = Math.max(Math.abs(shape.y2 - shape.y1) / 2, 0.1);

        ctx.save();
        ctx.translate(cx, cy);
        ctx.scale(rx, ry);
        ctx.beginPath();
        ctx.arc(0, 0, 1, 0, Math.PI * 2);
        ctx.restore();
        // stroke outside the scale so the outline keeps an even thickness
        ctx.stroke();
    }

    // ------------------------------------------------------------- pointer io

    private pointFromEvent(event: any): ImageEditorPoint {
        const canvas = this.overlayCanvas;
        const rect = canvas.getBoundingClientRect();
        const source = (event.touches && event.touches.length)
            ? event.touches[0]
            : ((event.changedTouches && event.changedTouches.length) ? event.changedTouches[0] : event);

        const scaleX = canvas.width / (rect.width || 1);
        const scaleY = canvas.height / (rect.height || 1);

        return {
            x: (source.clientX - rect.left) * scaleX,
            y: (source.clientY - rect.top) * scaleY
        };
    }

    /** Holding shift locks lines to 45 degree steps and boxes/circles to 1:1. */
    private constrain(start: ImageEditorPoint, point: ImageEditorPoint): ImageEditorPoint {
        if (!this.shiftKey) {
            return point;
        }
        const dx = point.x - start.x;
        const dy = point.y - start.y;

        if (this.tool === 'line' || this.tool === 'arrow') {
            const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
            const len = Math.sqrt(dx * dx + dy * dy);
            return { x: start.x + Math.cos(angle) * len, y: start.y + Math.sin(angle) * len };
        }

        const size = Math.max(Math.abs(dx), Math.abs(dy));
        return {
            x: start.x + (dx < 0 ? -size : size),
            y: start.y + (dy < 0 ? -size : size)
        };
    }

    onPointerDown(event: any) {
        if (this.loading || this.loadFailed || this.saving) {
            return;
        }
        if (event.button !== undefined && event.button !== 0) {
            return;
        }
        event.preventDefault();

        if (this.cropMode) {
            this.cropPointerDown(this.pointFromEvent(event));
            return;
        }

        this.shiftKey = !!event.shiftKey;
        this.drawing = true;
        this.start = this.pointFromEvent(event);
        this.current = this.start;
        this.freePoints = this.tool === 'free' ? [this.start] : [];
    }

    onPointerMove(event: any) {
        if (this.cropMode) {
            if (this.loading || this.loadFailed) {
                return;
            }
            const point = this.pointFromEvent(event);
            if (this.cropDrag) {
                event.preventDefault();
                this.cropPointerMove(point);
            } else {
                this.cropCursor = this.cursorForHandle(this.cropHandleAt(point));
            }
            return;
        }

        if (!this.drawing) {
            return;
        }
        event.preventDefault();
        this.shiftKey = event.shiftKey === undefined ? this.shiftKey : !!event.shiftKey;

        const point = this.pointFromEvent(event);
        if (this.tool === 'free') {
            const last = this.freePoints[this.freePoints.length - 1];
            // thin out the samples, raw pointer streams are far denser than needed
            if (!last || Math.abs(point.x - last.x) + Math.abs(point.y - last.y) >= 1.5) {
                this.freePoints.push(point);
            }
            this.current = point;
        } else {
            this.current = this.constrain(this.start, point);
        }

        this.clearOverlay();
        this.drawShape(this.overlayCanvas.getContext('2d'), this.buildShape());
    }

    onPointerUp(event: any) {
        if (this.cropMode) {
            if (this.cropDrag) {
                event.preventDefault();
                this.cropDrag = null;
            }
            return;
        }

        if (!this.drawing) {
            return;
        }
        event.preventDefault();
        this.drawing = false;

        if (event.type !== 'mouseleave') {
            const point = this.pointFromEvent(event);
            this.current = this.tool === 'free' ? point : this.constrain(this.start, point);
        }

        if (this.tool === 'free') {
            if (this.freePoints.length > 1) {
                this.shapes.push(this.buildShape());
            }
        } else {
            const dx = this.current.x - this.start.x;
            const dy = this.current.y - this.start.y;
            // ignore accidental taps
            if (Math.sqrt(dx * dx + dy * dy) >= 3) {
                this.shapes.push(this.buildShape());
            }
        }

        this.freePoints = [];
        this.redraw();
    }

    private buildShape(): ImageEditorShape {
        const shape: ImageEditorShape = {
            type: this.tool,
            x1: this.start.x,
            y1: this.start.y,
            x2: this.current.x,
            y2: this.current.y,
            color: this.color,
            width: this.strokeWidth
        };
        if (this.tool === 'free') {
            shape.points = this.freePoints.slice();
        }
        return shape;
    }

    // ------------------------------------------------------------------ crop

    startCrop() {
        if (!this.imageCanvas || this.loading || this.loadFailed) {
            return;
        }
        const w = this.imageCanvas.width;
        const h = this.imageCanvas.height;
        this.cropRect = { x: w * 0.1, y: h * 0.1, w: w * 0.8, h: h * 0.8 };
        this.cropDrag = null;
        this.cropMode = true;
        this.redraw();
    }

    cancelCrop() {
        this.cropMode = false;
        this.cropDrag = null;
        this.redraw();
    }

    applyCrop() {
        if (!this.cropMode || !this.imageCanvas) {
            return;
        }
        const x = Math.max(0, Math.round(this.cropRect.x));
        const y = Math.max(0, Math.round(this.cropRect.y));
        const w = Math.min(Math.round(this.cropRect.w), this.imageCanvas.width - x);
        const h = Math.min(Math.round(this.cropRect.h), this.imageCanvas.height - y);

        if (w < 1 || h < 1) {
            this.cancelCrop();
            return;
        }

        const cropped = document.createElement('canvas');
        cropped.width = w;
        cropped.height = h;
        cropped.getContext('2d').drawImage(this.imageCanvas, x, y, w, h, 0, 0, w, h);
        this.imageCanvas = cropped;

        // shapes live in image space, shift them with the new origin
        this.shapes.forEach((shape) => {
            shape.x1 -= x;
            shape.y1 -= y;
            shape.x2 -= x;
            shape.y2 -= y;
            if (shape.points) {
                shape.points.forEach((p) => {
                    p.x -= x;
                    p.y -= y;
                });
            }
        });

        this.cropped = true;
        this.cropMode = false;
        this.cropDrag = null;
        this.syncCanvasSize();
        this.redraw();
    }

    get cropSizeLabel(): string {
        return Math.round(this.cropRect.w) + ' x ' + Math.round(this.cropRect.h);
    }

    private cropPointerDown(point: ImageEditorPoint) {
        const handle = this.cropHandleAt(point);
        if (handle === 'new') {
            this.cropRect = { x: point.x, y: point.y, w: 0, h: 0 };
        }
        this.cropDrag = {
            handle: handle,
            from: point,
            orig: { x: this.cropRect.x, y: this.cropRect.y, w: this.cropRect.w, h: this.cropRect.h }
        };
        this.cropCursor = this.cursorForHandle(handle);
    }

    private cropPointerMove(point: ImageEditorPoint) {
        const imgW = this.imageCanvas.width;
        const imgH = this.imageCanvas.height;
        const orig = this.cropDrag.orig;
        const dx = point.x - this.cropDrag.from.x;
        const dy = point.y - this.cropDrag.from.y;

        if (this.cropDrag.handle === 'new') {
            const x1 = this.clamp(this.cropDrag.from.x, 0, imgW);
            const y1 = this.clamp(this.cropDrag.from.y, 0, imgH);
            const x2 = this.clamp(point.x, 0, imgW);
            const y2 = this.clamp(point.y, 0, imgH);
            this.cropRect = {
                x: Math.min(x1, x2),
                y: Math.min(y1, y2),
                w: Math.abs(x2 - x1),
                h: Math.abs(y2 - y1)
            };
            this.repaintCrop();
            return;
        }

        if (this.cropDrag.handle === 'move') {
            this.cropRect = {
                x: this.clamp(orig.x + dx, 0, imgW - orig.w),
                y: this.clamp(orig.y + dy, 0, imgH - orig.h),
                w: orig.w,
                h: orig.h
            };
            this.repaintCrop();
            return;
        }

        const min = ImageEditorComponent.MIN_CROP;
        let left = orig.x;
        let top = orig.y;
        let right = orig.x + orig.w;
        let bottom = orig.y + orig.h;

        if (this.cropDrag.handle.indexOf('w') >= 0) {
            left = this.clamp(orig.x + dx, 0, right - min);
        }
        if (this.cropDrag.handle.indexOf('e') >= 0) {
            right = this.clamp(orig.x + orig.w + dx, left + min, imgW);
        }
        if (this.cropDrag.handle.indexOf('n') >= 0) {
            top = this.clamp(orig.y + dy, 0, bottom - min);
        }
        if (this.cropDrag.handle.indexOf('s') >= 0) {
            bottom = this.clamp(orig.y + orig.h + dy, top + min, imgH);
        }

        this.cropRect = { x: left, y: top, w: right - left, h: bottom - top };
        this.repaintCrop();
    }

    /** The base canvas does not change while framing a crop, only repaint the overlay. */
    private repaintCrop() {
        this.clearOverlay();
        this.drawCropOverlay();
    }

    /** Which crop handle (if any) sits under the pointer. */
    private cropHandleAt(point: ImageEditorPoint): string {
        const tol = 12 * this.displayRatio;
        const r = this.cropRect;
        if (r.w <= 0 || r.h <= 0) {
            return 'new';
        }

        const onLeft = Math.abs(point.x - r.x) <= tol;
        const onRight = Math.abs(point.x - (r.x + r.w)) <= tol;
        const onTop = Math.abs(point.y - r.y) <= tol;
        const onBottom = Math.abs(point.y - (r.y + r.h)) <= tol;
        const withinX = point.x >= r.x - tol && point.x <= r.x + r.w + tol;
        const withinY = point.y >= r.y - tol && point.y <= r.y + r.h + tol;

        if (onLeft && onTop) { return 'nw'; }
        if (onRight && onTop) { return 'ne'; }
        if (onLeft && onBottom) { return 'sw'; }
        if (onRight && onBottom) { return 'se'; }
        if (onLeft && withinY) { return 'w'; }
        if (onRight && withinY) { return 'e'; }
        if (onTop && withinX) { return 'n'; }
        if (onBottom && withinX) { return 's'; }
        if (withinX && withinY) { return 'move'; }
        return 'new';
    }

    private cursorForHandle(handle: string): string {
        switch (handle) {
            case 'nw':
            case 'se': return 'nwse-resize';
            case 'ne':
            case 'sw': return 'nesw-resize';
            case 'n':
            case 's': return 'ns-resize';
            case 'e':
            case 'w': return 'ew-resize';
            case 'move': return 'move';
            default: return 'crosshair';
        }
    }

    private drawCropOverlay() {
        const canvas = this.overlayCanvas;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        const r = this.cropRect;
        // keep the chrome a constant thickness on screen whatever the zoom
        const px = this.displayRatio;

        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        if (r.w > 0 && r.h > 0) {
            ctx.clearRect(r.x, r.y, r.w, r.h);
        }

        if (r.w > 0 && r.h > 0) {
            // rule of thirds
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
            ctx.lineWidth = px;
            ctx.beginPath();
            for (let i = 1; i < 3; i++) {
                ctx.moveTo(r.x + (r.w / 3) * i, r.y);
                ctx.lineTo(r.x + (r.w / 3) * i, r.y + r.h);
                ctx.moveTo(r.x, r.y + (r.h / 3) * i);
                ctx.lineTo(r.x + r.w, r.y + (r.h / 3) * i);
            }
            ctx.stroke();

            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = px * 1.5;
            ctx.strokeRect(r.x, r.y, r.w, r.h);

            // corner grips
            const grip = px * 14;
            const thick = px * 4;
            ctx.fillStyle = '#ffffff';
            const corners = [
                { x: r.x, y: r.y, sx: 1, sy: 1 },
                { x: r.x + r.w, y: r.y, sx: -1, sy: 1 },
                { x: r.x, y: r.y + r.h, sx: 1, sy: -1 },
                { x: r.x + r.w, y: r.y + r.h, sx: -1, sy: -1 }
            ];
            corners.forEach((c) => {
                ctx.fillRect(
                    c.sx > 0 ? c.x : c.x - grip,
                    c.sy > 0 ? c.y : c.y - thick,
                    grip,
                    thick
                );
                ctx.fillRect(
                    c.sx > 0 ? c.x : c.x - thick,
                    c.sy > 0 ? c.y : c.y - grip,
                    thick,
                    grip
                );
            });
        }
        ctx.restore();
    }

    private clamp(value: number, min: number, max: number): number {
        return Math.min(Math.max(value, min), Math.max(min, max));
    }

    // ---------------------------------------------------------------- toolbar

    selectTool(tool: ImageEditorTool) {
        if (this.cropMode) {
            return;
        }
        this.tool = tool;
    }

    selectColor(color: string) {
        this.color = color;
    }

    selectWidth(width: number) {
        this.strokeWidth = width;
        this.storeStrokeWidth(width);
    }

    /**
     * The stroke width sticks per browser so an inspector keeps the same pen
     * across every finding image they edit, not just the open modal.
     */
    private restoreStrokeWidth() {
        try {
            const stored = parseInt(localStorage.getItem(ImageEditorComponent.WIDTH_KEY), 10);
            // only honour a width still offered by the toolbar
            if (this.widths.some((w) => w.value === stored)) {
                this.strokeWidth = stored;
            }
        } catch (e) {
            // private mode / blocked storage, keep the default
        }
    }

    private storeStrokeWidth(width: number) {
        try {
            localStorage.setItem(ImageEditorComponent.WIDTH_KEY, String(width));
        } catch (e) {
            // nothing to do, the width still applies to this session
        }
    }

    get canUndo(): boolean {
        return this.shapes.length > 0;
    }

    get shapeCount(): number {
        return this.shapes.length;
    }

    /** Pixel size of the image as it will be saved, for the status bar. */
    get outputSize(): string {
        return this.imageCanvas ? this.imageCanvas.width + ' x ' + this.imageCanvas.height : '';
    }

    undo() {
        this.shapes.pop();
        this.redraw();
    }

    clearShapes() {
        this.shapes = [];
        this.redraw();
    }

    rotateLeft() {
        this.rotate(-1);
    }

    rotateRight() {
        this.rotate(1);
    }

    private rotate(direction: number) {
        if (!this.imageCanvas || this.loading || this.cropMode) {
            return;
        }
        const w = this.imageCanvas.width;
        const h = this.imageCanvas.height;

        this.rotateImageCanvas(direction);
        // carry the annotations along with the image
        this.shapes.forEach((shape) => {
            const a = this.rotatePoint(shape.x1, shape.y1, direction, w, h);
            const b = this.rotatePoint(shape.x2, shape.y2, direction, w, h);
            shape.x1 = a.x;
            shape.y1 = a.y;
            shape.x2 = b.x;
            shape.y2 = b.y;
            if (shape.points) {
                shape.points = shape.points.map((p) => this.rotatePoint(p.x, p.y, direction, w, h));
            }
        });

        this.rotation = (this.rotation + direction * 90) % 360;
        this.syncCanvasSize();
        this.redraw();
    }

    private rotateImageCanvas(direction: number) {
        const src = this.imageCanvas;
        const w = src.width;
        const h = src.height;

        const rotated = document.createElement('canvas');
        rotated.width = h;
        rotated.height = w;

        const ctx = rotated.getContext('2d');
        ctx.translate(h / 2, w / 2);
        ctx.rotate((direction * 90 * Math.PI) / 180);
        ctx.drawImage(src, -w / 2, -h / 2);

        this.imageCanvas = rotated;
    }

    /** w/h are the pre-rotation image dimensions. */
    private rotatePoint(x: number, y: number, direction: number, w: number, h: number): ImageEditorPoint {
        return direction > 0
            ? { x: h - y, y: x }   // clockwise
            : { x: y, y: w - x };  // counter clockwise
    }

    // ----------------------------------------------------------------- output

    apply() {
        const canvas = this.baseCanvas;
        if (!canvas || this.saving || this.loading || this.loadFailed || this.cropMode) {
            return;
        }
        this.saving = true;

        const type = this.mimeType;
        const quality = type === 'image/jpeg' ? 0.92 : undefined;
        const dataUrl = canvas.toDataURL(type, quality);

        const done = (blob: Blob) => {
            this.saving = false;
            this.activeModal.close({
                dataUrl: dataUrl,
                blob: blob,
                file: this.toFile(blob),
                rotation: this.rotation,
                annotated: this.shapes.length > 0,
                cropped: this.cropped
            } as ImageEditorResult);
        };

        if (canvas.toBlob) {
            canvas.toBlob((blob) => done(blob || this.dataUrlToBlob(dataUrl)), type, quality);
        } else {
            done(this.dataUrlToBlob(dataUrl));
        }
    }

    cancel() {
        this.activeModal.dismiss('cancel');
    }

    private toFile(blob: Blob): File {
        const type = blob.type || this.mimeType;
        try {
            return new File([blob], this.fileName, { type: type });
        } catch (e) {
            // older browsers have no File constructor, a tagged Blob is close enough
            const file: any = blob;
            file.name = this.fileName;
            file.lastModifiedDate = new Date();
            return file as File;
        }
    }

    private dataUrlToBlob(dataUrl: string): Blob {
        const parts = dataUrl.split(',');
        const matched = parts[0].match(/:(.*?);/);
        const mime = matched ? matched[1] : this.mimeType;
        const binary = atob(parts[1]);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return new Blob([bytes], { type: mime });
    }
}
